import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate'

/* global app */
const getDefaultState = () => {
  return {
    useBeta: false,
    showBetaNoticeOnTopOfScreen: true,
    dataStructureVersion: null,
    showSearch: false,
    showGlobalChildFilter: false,
    showMainNavbar: true,
    showSideNavbar: false,
    activeRequestCount: 0,
    deviceToken: null,
    session: null,
    applicationSettings: null,
    establishments: null,
    groups: null,
    children: null,
    childrenPresence: [],
    globalChildByIdFilter: [],
    globalChildByTextFilter: '',
    employees: null,
    groupActivities: null,
    groupActivityTypes: null,
    birthdays: null,
    mutations: [],
    planning: null,
    planningRatio: null,
    selectedEstablishment: null,
    selectedGroup: null,
    selectedGroups: null,
    selectedGroupsQueryString: '',
    selectedChild: null,
    gruopMessages: null,
    groupInstructions: [],
    transferNotes: [],
    transferNotesOffset: 0,
    specialCareNotes: [],
    imageTokenExpiresAt: null,
    imageToken: null,
    settingsRefreshAt: null,
    orderChildrenBy: null,
    orderChildrenSort: null,
    hasUnreadChatMessages: false,
    unreadChatMessageCountPerChild: [],
    datePlanning: null,
  }
}

const state = getDefaultState()

const getters = {
  useBeta (state) {
    return state.useBeta
  },
  dataStructureVersion (state) {
    return state.dataStructureVersion
  },
  activeRequestCount (state) {
    return state.activeRequestCount
  },
  deviceToken (state) {
    return state.deviceToken
  },
  session (state) {
    return state.session
  },
  showSearch (state) {
    return state.showSearch
  },
  showGlobalChildFilter (state) {
    return state.showGlobalChildFilter
  },
  showMainNavbar (state) {
    return state.showMainNavbar
  },
  showSideNavbar (state) {
    return state.showSideNavbar
  },
  applicationSettings (state) {
    return state.applicationSettings
  },
  applicationSetting: (state) => (name) => {
    if (state.applicationSettings) {
      var setting = state.applicationSettings.find(function (setting) {
        return setting.name === name
      })
      if (setting && setting.value) {
        return setting.value
      }
    }
    return false
  },
  establishments (state) {
    return state.establishments
  },
  groups (state) {
    return state.groups
  },
  children (state) {
    return state.children
  },
  childrenPresence (state) {
    return state.childrenPresence
  },
  groupInstructions (state) {
    return state.groupInstructions
  },
  globalChildByIdFilter (state) {
    return state.globalChildByIdFilter
  },
  globalChildByTextFilter (state) {
    return state.globalChildByTextFilter
  },
  employees (state) {
    return state.employees
  },
  groupActivities (state) {
    return state.groupActivities
  },
  groupActivityTypes (state) {
    return state.groupActivityTypes
  },
  birthdays (state) {
    return state.birthdays
  },
  mutations (state) {
    return state.mutations
  },
  planning (state) {
    return state.planning
  },
  planningRatio (state) {
    return state.planningRatio
  },
  selectedEstablishment (state) {
    return state.selectedEstablishment
  },
  selectedGroup (state) {
    return state.selectedGroup
  },
  selectedGroups (state) {
    return state.selectedGroups
  },
  selectedGroupsQueryString (state) {
    return state.selectedGroupsQueryString
  },
  selectedChild (state) {
    return state.selectedChild
  },
  transferNotes (state) {
    return state.transferNotes
  },
  transferNotesOffset (state) {
    return state.transferNotesOffset
  },
  specialCareNotes (state) {
    return state.specialCareNotes
  },
  imageTokenExpiresAt (state) {
    return state.imageTokenExpiresAt
  },
  imageToken (state) {
    return state.imageToken
  },
  settingsRefreshAt (state) {
    return state.settingsRefreshAt
  },
  orderChildrenBy (state) {
    return state.orderChildrenBy
  },
  orderChildrenSort (state) {
    return state.orderChildrenSort
  },
  hasUnreadChatMessages (state) {
    return state.hasUnreadChatMessages
  },
  unreadChatMessageCountPerChild (state) {
    return state.unreadChatMessageCountPerChild
  },
  datePlanning(state) {
    return state.datePlanning
  }
}

const mutations = {
  RESET_STATE (state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState())
  },
  SET_USE_BETA: (state, data) => {
    state.useBeta = data
  },
  SET_DATA_STRUCTURE_VERSION: (state, data) => {
    state.dataStructureVersion = data
  },
  SET_ERROR: () => {
    return
  },
  RESET_ACTIVE_REQUEST_COUNT: (state) => {
    state.activeRequestCount = 0
  },
  REGISTER_ACTIVE_REQUEST: (state) => {
    state.activeRequestCount++
  },
  REMOVE_ACTIVE_REQUEST: (state) => {
    state.activeRequestCount--
  },
  SET_DEVICE_TOKEN: (state, data) => {
    state.deviceToken = data
  },
  SET_SESSION: (state, data) => {
    if (data) {
      state.session = data
    } else {
      state.session = null
    }
  },
  SET_APPLICATION_SETTINGS: (state, data) => {
    if (data) {
      state.applicationSettings = data
    } else {
      state.applicationSettings = null
    }
  },
  SET_SHOW_SEARCH: (state, data) => {
    if (data === true) {
      state.showSearch = true
    } else {
      state.showSearch = false
    }
  },
  SET_SHOW_GLOBAL_CHILD_FILTER: (state, data) => {
    if (data === true) {
      state.showGlobalChildFilter = true
    } else {
      state.showGlobalChildFilter = false
    }
  },
  SET_SHOW_MAIN_NAVBAR: (state, data) => {
    if (data === true) {
      state.showMainNavbar = true
    } else {
      state.showMainNavbar = false
    }
  },
  SET_SHOW_SIDE_NAVBAR: (state, data) => {
    if (data === true) {
      state.showSideNavbar = true
    } else {
      state.showSideNavbar = false
    }
  },
  SET_ESTABLISHMENTS: (state, data) => {
    if (data) {
      state.establishments = data
    } else {
      state.establishments = null
    }
  },
  SET_GROUPS: (state, data) => {
    if (data) {
      state.groups = data
      if (Array.isArray(data)) {
        var groups = data
        var selectedGroups = JSON.parse(JSON.stringify(state.selectedGroups))
        var selectedGroupsCopy = JSON.parse(JSON.stringify(state.selectedGroups))
        selectedGroups.forEach(function(selectedGroup, i) {
          groups.forEach(function(group) {
            if (selectedGroup.id === group.id) {
              if (JSON.stringify(selectedGroup) !== JSON.stringify(group)) {
                selectedGroups[i] = group
              }
            }
          })
        })
        if (JSON.stringify(selectedGroups) !== JSON.stringify(selectedGroupsCopy)) {
          state.selectedGroups = JSON.parse(JSON.stringify(selectedGroups))
        }
      }
    } else {
      state.groups = null
    }
  },
  SET_CHILDREN: (state, data) => {
    if (data) {
      state.children = data
    } else {
      state.children = null
    }
  },
  ADD_OR_UPDATE_CHILD(state, data) {
    if (data === 'Invalid parameters') {
      return true;
    }
    const index = state.children.findIndex(child => {
      return child.id == data.id
    })

    if (index !== -1) {
      if (state.children[index].reset) {
        data.reset = state.children[index].reset + 1
      } else {
        data.reset = 0
      }

      state.children[index] = data
    } else {
      data.reset = 0
      state.children.push(data)
    }
  },
  SET_CHILDREN_PRESENCE: (state, data) => {
    if (data) {
      state.childrenPresence = data
    } else {
      state.childrenPresence = null
    }
  },
  SET_GROUP_INSTRUCTIONS: (state, data) => {
    if (data) {
      state.groupInstructions = data
    } else {
      state.groupInstructions = null
    }
  },
  SET_GLOBAL_CHILD_BY_ID_FILTER: (state, data) => {
    if (data) {
      state.globalChildByIdFilter = data
    } else {
      state.globalChildByIdFilter = []
    }
  },
  SET_GLOBAL_CHILD_BY_TEXT_FILTER: (state, data) => {
    if (data) {
      state.globalChildByTextFilter = data
    } else {
      state.globalChildByTextFilter = ''
    }
  },
  SET_EMPLOYEES: (state, data) => {
    if (data) {
      state.employees = data
    } else {
      state.employees = null
    }
  },
  SET_GROUP_ACTIVITIES: (state, data) => {
    if (data) {
      state.groupActivities = data
    } else {
      state.groupActivities = null
    }
  },
  SET_GROUP_ACTIVITY_TYPES: (state, data) => {
    if (data) {
      state.groupActivityTypes = data
    } else {
      state.groupActivityTypes = null
    }
  },
  SET_BIRTHDAYS: (state, data) => {
    if (data) {
      state.birthdays = data
    } else {
      state.birthdays = null
    }
  },
  SET_MUTATIONS: (state, data) => {
    if (data) {
      state.mutations = data
    } else {
      state.mutations = []
    }
  },
  SET_PLANNING: (state, data) => {
    if (data) {
      state.planning = data
    } else {
      state.planning = null
    }
  },
  SET_PLANNING_RATIO: (state, data) => {
    if (data) {
      state.planningRatio = data
    } else {
      state.planningRatio = null
    }
  },
  SET_SELECTED_ESTABLISHMENT: (state, data) => {
    if (data) {
      state.selectedEstablishment = data
    } else {
      state.selectedEstablishment = null
    }
    state.groups = []
    state.selectedGroup = null
    state.selectedGroups = null
  },
  SET_SELECTED_GROUPS: (state, data) => {
    if (data) {
      state.selectedGroups = data
    } else {
      state.selectedGroups = null
    }
    if (data) {
      var queryString = '';
      if (state.selectedGroups.length) {
        queryString = state.selectedGroups.map((group) => group.idGroup).join(',')
      }
      state.selectedGroupsQueryString = queryString
    }
    // @toodo Zoek mooie plek
    state.children = null
    state.planning = null
    state.groupActivities = null
    state.groupActivityTypes = null
    state.groupMessages = null
    state.selectedChild = null
    state.transferNotes = null
    state.globalChildByIdFilter = []
    state.birthdays = null
    state.groupInstructions = []
    state.showMainNavbar = true
    state.showGlobalChildFilter = false
    state.hasUnreadChatMessages = false
  },
  SET_SELECTED_CHILD: (state, data) => {
    if (data) {
      state.selectedChild = data
    } else {
      state.selectedChild = null
    }
  },
  SET_TRANSFER_NOTES: (state, data) => {
    if (data) {
      let transferNotes = state.transferNotes || []

      for (let i = 0; i < data.length; i++) {
        let index = transferNotes.findIndex(function (note) {
          return note.id == data[i].id
        })

        if (index !== -1) {
          transferNotes[index] = data[i]
        } else {
          transferNotes.push(data[i])
        }
      }

      state.transferNotes = []
      state.transferNotes = transferNotes
    } else {
      state.transferNotes = []
    }
  },
  DELETE_TRANSFER_NOTE_BY_ID: (state, id) => {
    let transferNotes = state.transferNotes || []
    transferNotes = transferNotes.filter(function(item) {
      return parseInt(item.id) !== parseInt(id)
    })
    state.transferNotes = []
    state.transferNotes = transferNotes
  },
  SET_TRANSFER_NOTES_OFFSET: (state, offset) => {
    state.transferNotesOffset = offset
  },
  SET_SPECIAL_CARE_NOTES: (state, data) => {
    if (data) {
      state.specialCareNotes = data
    } else {
      state.specialCareNotes = null
    }
  },
  UPDATE_CHILD_INFO(state, data) {

    const index = state.children.findIndex((child) => {
      return child.id == data.idChild
    })

    let child = state.children[index];

    if (data.name !== '' && data.name !== false ) {
      child[data.name] = data.value
    }

    if (index != -1) {
      if (!child.reset) {
        child.reset = 0;
      }

      child.reset += 1;
      state.children[index] = child;
    }
  },
  SET_IMAGE_TOKEN_EXPIRES_AT(state, datetime) {
    state.imageTokenExpiresAt = datetime
  },
  SET_IMAGE_TOKEN(state, token) {
    state.imageToken = token
  },
  SET_SETTINGS_REFRESH_AT(state, datetime) {
    state.settingsRefreshAt = datetime
  },
  SET_DATE_PLANNING(state, date) {
    state.datePlanning = date
  },
  SET_ORDER_CHILDREN_BY(state, propertyName) {
    state.orderChildrenBy = propertyName
  },
  SET_ORDER_CHILDREN_SORT(state, order) {
    state.orderChildrenSort = order
  },
  SET_HAS_UNREAD_CHAT_MESSAGES (state, hasUnreadMessages) {
    state.hasUnreadChatMessages = hasUnreadMessages
  },
  SET_UNREAD_CHAT_MESSAGE_COUNT_PER_CHILD (state, data) {
    state.unreadChatMessageCountPerChild = data
  }
}

const actions = {
  loadApplicationSettings (context, callback) {
    callback = callback || function () {}
    const idCompany = this.state.selectedEstablishment?.idCompany ?? 1
    const idEstablishment = this.state.selectedEstablishment?.id ?? 0
    return app.config.globalProperties.$request.get('/settings/' + idCompany + '/' + idEstablishment)
        .then(response => {
          if (response.data) {
            context.commit('SET_SETTINGS_REFRESH_AT', app.config.globalProperties.$moment().add(1, 'days').format('YYYY-MM-DD HH:mm'))
            context.commit('SET_APPLICATION_SETTINGS', response.data)
          }
          callback()
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getEstablishments (context) {
    app.config.globalProperties.$request.get('/establishments')
        .then(response => {
          if (response.data) {
            context.commit('SET_ESTABLISHMENTS', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getGroups (context) {
    if (!this.state.selectedEstablishment) {
      return
    }
    app.config.globalProperties.$request.get('/groups?id_establishment=' + this.state.selectedEstablishment.id)
        .then(response => {
          if (response.data) {
            context.commit('SET_GROUPS', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getChildren (context) {
    // showPresentChildrenOnly = showPresentChildrenOnly || false
    // var query = ''
    // if (showPresentChildrenOnly) {
    //   query += '&by_presence=true'
    // }
    app.config.globalProperties.$request.get('/children?id_group=' + context.state.selectedGroupsQueryString)
        .then(response => {
          if (response.data) {
            for (var i = 0; i < response.data.length; i++) {
              response.data[i].id_child = response.data[i].id || null
            }
            context.commit('SET_CHILDREN', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getChildrenPresence (context) {
    app.config.globalProperties.$request.get('/events/current-presence?id_group=' + context.state.selectedGroupsQueryString)
        .then(response => {
          if (response.data) {
            context.commit('SET_CHILDREN_PRESENCE', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getEmployees (context, filterByGroupAndDate) {
    var query = ''
    if (filterByGroupAndDate) {
      query += 'id_group=' + context.state.selectedGroupsQueryString
      query += '&date=' + app.$moment().format('YYYY-MM-DD')
    }
    app.config.globalProperties.$request.get('/employees?' + query)
        .then((response) => {
          if (response.data) {
            context.commit('SET_EMPLOYEES', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getGroupActivities (context, date) {
    date = date || app.$moment().format('YYYY-MM-DD')
    app.config.globalProperties.$request.get('/activities?id_group=' + context.state.selectedGroupsQueryString + '&date=' + date)
        .then(response => {
          if (response.data) {
            context.commit('SET_GROUP_ACTIVITIES', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getGroupInstructions (context) {
    app.config.globalProperties.$request.get('/instructions?id_group=' + context.state.selectedGroupsQueryString)
        .then(response => {
          if (response.data) {
            context.commit('SET_GROUP_INSTRUCTIONS', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getGroupActivityTypes (context) {
    app.config.globalProperties.$request.get('/activities/types?id_group=' + context.state.selectedGroupsQueryString)
        .then(response => {
          if (response.data) {
            context.commit('SET_GROUP_ACTIVITY_TYPES', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getBirthdays (context) {
    var dateStart = app.$moment().format('YYYY-MM-DD')
    var dateEnd = app.$moment().add(14, 'days').format('YYYY-MM-DD')
    app.config.globalProperties.$request.get('/events/birthdays?id_group=' + context.state.selectedGroupsQueryString + '&start=' + dateStart + '&end=' + dateEnd)
        .then(response => {
          if (response.data) {
            context.commit('SET_BIRTHDAYS', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getMutations (context) {
    var dateStart = app.$moment().format('YYYY-MM-DD')
    var dateEnd = app.$moment().add(14, 'days').format('YYYY-MM-DD')
    app.config.globalProperties.$request.get('/events/schedule-mutations?id_group=' + context.state.selectedGroupsQueryString + '&start=' + dateStart + '&end=' + dateEnd)
        .then(response => {
          if (response.data) {
            context.commit('SET_MUTATIONS', response.data)
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getPlanning (context, date) {
    // planning
    app.config.globalProperties.$request.get('/planning?id_group=' + context.state.selectedGroupsQueryString + '&date=' + date)
        .then(response => {
          if (response.data) {
            context.commit('SET_PLANNING', response.data)
          } else {
            context.commit('SET_PLANNING', [])
            context.commit('SET_PLANNING_RATIO', [])
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
    // ratio
    app.config.globalProperties.$request.get('/ratio?id_group=' + context.state.selectedGroupsQueryString + '&date=' + date)
        .then(response => {
          if (response.data) {
            context.commit('SET_PLANNING_RATIO', response.data)
          } else {
            context.commit('SET_PLANNING_RATIO', [])
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  getTransferNotes (context, data) {
    let url = '/transfer-notes'
    let reset = data.reset || false
    if (data.id) {
      url += '/' + data.id
    } else {
      url += '?id_group=' + context.state.selectedGroupsQueryString
      if (data.date) {
        context.commit('SET_TRANSFER_NOTES', [])
        url += '&date=' + data.date
      }
      if (!isNaN(data.limit)) {
        url += '&limit=' + data.limit
      }
      if (!isNaN(data.offset)) {
        url += '&offset=' + data.offset
      }
    }
    app.config.globalProperties.$request.get(url)
        .then(response => {
          if (reset && reset === true) {
            context.commit('SET_TRANSFER_NOTES', [])
          }
          if (response.data) {
            if (
                typeof response.data === 'object'
                &&
                !Array.isArray(response.data)
                &&
                response.data !== null
            ) {
              context.commit('SET_TRANSFER_NOTES', [response.data])
            } else {
              context.commit('SET_TRANSFER_NOTES', response.data)
            }
          } else {
            context.commit('SET_TRANSFER_NOTES', [])
          }
        }).catch((error) => {
      context.commit('SET_ERROR', error)
    })
  },
  updateChildInfo(context, idChild, name, value) {
    context.commit('UPDATE_CHILD_INFO', {
      idChild: idChild,
      name: name,
      value: value,
    });
  },
  updateHasUnreadChatMessages (context) {
    app.config.globalProperties.$request.get('/events/has-unread-messages?id_group=' + context.state.selectedGroupsQueryString)
    .then(response => {
      if (response.data && response.data.has_unread_messages) {
        if (response.data.has_unread_messages === true) {
          context.commit('SET_HAS_UNREAD_CHAT_MESSAGES', true)
        } else {
          context.commit('SET_HAS_UNREAD_CHAT_MESSAGES', false)
        }
      } else {
        context.commit('SET_HAS_UNREAD_CHAT_MESSAGES', false)
      }
    })
  },
  updateUnreadChatMessageCountPerChild (context) {
    app.config.globalProperties.$request.get('/messages/unread-message-count?id_group=' + context.state.selectedGroupsQueryString)
    .then(response => {
      if (response.data) {
          context.commit('SET_UNREAD_CHAT_MESSAGE_COUNT_PER_CHILD', response.data)
      }
    })
  },
  updateDatePlanning(context, date){
    context.commit('SET_DATE_PLANNING', date)
  }
}

const plugins = [createPersistedState({
  key: 'ka-groepsapp',
  // Only store specific keys in local storage
  reducer: state => ({
    activeRequestCount: state.activeRequestCount,
    deviceToken: state.deviceToken,
    session: state.session,
    applicationSettings: state.applicationSettings,
    establishments: state.establishments,
    groups: state.groups,
    children: state.children,
    childrenPresence: state.childrenPresence,
    globalChildByIdFilter: state.globalChildByIdFilter,
    globalChildByTextFilter: state.globalChildByTextFilter,
    employees: state.employees,
    groupActivities: state.groupActivities,
    groupActivityTypes: state.groupActivityTypes,
    planning: state.planning,
    planningRatio: state.planningRatio,
    selectedEstablishment: state.selectedEstablishment,
    selectedGroup: state.selectedGroup,
    selectedGroups: state.selectedGroups,
    selectedGroupsQueryString: state.selectedGroupsQueryString,
    selectedChild: state.selectedChild,
    // gruopMessages: null,
    // transferNotes: null,
    specialCareNotes: state.specialCareNotes,
    birthdays: state.birthdays,
    mutations: state.mutations,
    groupInstructions: state.groupInstructions,
    useBeta: state.useBeta,
    imageTokenExpiresAt: state.imageTokenExpiresAt,
    imageToken: state.imageToken,
    settingsRefreshAt: state.settingsRefreshAt,
    dataStructureVersion: state.dataStructureVersion,
    orderChildrenBy: state.orderChildrenBy,
    orderChildrenSort: state.orderChildrenSort,
    unreadChatMessageCountPerChild: state.unreadChatMessageCountPerChild
  })
})]

export const store = createStore({
  state,
  getters,
  mutations,
  actions,
  plugins
})
